/**
 * PlayerProvider — shared context setup for both Editor and ExportPage.
 * Provides: TimelineContext, SetTimelineContext, CompositionManager,
 * SharedAudioContext, DurationsContext, KeylistenerContext.
 * Also sets window.picmaker_* globals for the renderer.
 */
import React, { useState, useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import { TimelineContext, SetTimelineContext } from './timeline-position-state';
import { KeylistenerContext } from './keylistener-context';
import { CompositionManager } from './composition-context';
import { DurationsContextProvider } from './video/duration-state';
import { SharedAudioContextProvider } from './audio/shared-audio-tags';
import { delayRender, continueRender } from './delay-render';

const PlayerProvider = ({ children }) => {
  const [frame, setFrame] = useState(0);
  const committedFrameRef = useRef(0); // last frame React actually committed
  const frameHandleRef = useRef(null); // active delayRender handle for current setFrame call
  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [loop, setLoop] = useState(false);
  const [reverse, setReverse] = useState(false);
  const [muted, setMuted] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [keyPressed, setKeyPressed] = useState(0);
  const [audioAndVideoTags] = useState({ current: [] });
  const [assets, setAssets] = useState([]);
  const [sequences, setSequences] = useState([]);

  const registerAsset = useCallback((asset) => {
    setAssets((a) => [...a, asset]);
  }, []);
  const unregisterAsset = useCallback((id) => {
    setAssets((a) => a.filter((x) => x.id !== id));
  }, []);
  const registerSequence = useCallback((seq) => {
    setSequences((s) => [...s, seq]);
  }, []);
  const unregisterSequence = useCallback((seq) => {
    setSequences((s) => s.filter((x) => x.id !== seq));
  }, []);

  const compositionValue = useMemo(() => ({
    registerAsset, unregisterAsset, sequences, assets,
  }), [registerSequence, unregisterSequence, registerAsset, unregisterAsset, sequences, assets]);

  const timelineValue = useMemo(() => ({
    frame, playing, playbackRate, setPlaybackRate,
    audioAndVideoTags, loop, setLoop, reverse, setReverse, muted, setMuted,
    buffering, setBuffering,
  }), [frame, playbackRate, playing, loop, reverse, audioAndVideoTags, muted, buffering]);

  const setTimelineValue = useMemo(() => ({
    setFrame, setPlaying,
  }), []);

  // Resolve the frame-setting handle after React commits.
  // useLayoutEffect runs after ALL child useLayoutEffect effects, so by the time
  // this fires, OffthreadVideoForRendering has already called delayRender(imgHandle).
  // Resolving frameHandle here leaves only imgHandle in pendingHandles, so
  // window.ready becomes true only once the proxy image actually loads.
  // For frames with no video the imgHandle is never created, so resolving
  // frameHandle alone sets window.ready = true immediately — correct behaviour.
  useLayoutEffect(() => {
    committedFrameRef.current = frame;
    if (frameHandleRef.current !== null) {
      continueRender(frameHandleRef.current);
      frameHandleRef.current = null;
    }
  }, [frame]);

  // Set window globals for the renderer
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    window.picmaker_setFrame = (f) => {
      if (f === committedFrameRef.current) {
        // Frame unchanged — window.ready is already true; push the notification manually
        // because delay-render.js won't fire (no handles changed).
        window.picmaker_frameReady?.();
        return;
      }
      const id = delayRender(`Setting the current frame to ${f}`);
      frameHandleRef.current = id;
      setFrame(f);
    };
    window.picmaker_isPlayer = false;
  }, []);

  useLayoutEffect(() => {
    window.picmaker_collectAssets = () => {
      setAssets([]);
      return assets;
    };
  }, [assets]);

  return (
    <KeylistenerContext.Provider value={{ keyPressed, setKeyPressed }}>
      <TimelineContext.Provider value={timelineValue}>
        <SetTimelineContext.Provider value={setTimelineValue}>
          <CompositionManager.Provider value={compositionValue}>
            <SharedAudioContextProvider numberOfAudioTags={0} component={null}>
              <DurationsContextProvider>
                {children}
              </DurationsContextProvider>
            </SharedAudioContextProvider>
          </CompositionManager.Provider>
        </SetTimelineContext.Provider>
      </TimelineContext.Provider>
    </KeylistenerContext.Provider>
  );
};

export default PlayerProvider;
